import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  Cable,
  FileText,
  LayoutGrid,
  ListChecks,
  PanelsTopLeft,
  Plus,
  SquareStack,
  X,
} from 'lucide-react';
import { useStore } from '../store';
import type { SheetKind } from '../types';
import { ConfirmModal, NameModal, PickKindModal, type AddDialog } from './SheetModals';

function iconForKind(kind: SheetKind) {
  if (kind === 'block') return LayoutGrid;
  if (kind === 'mechanical') return PanelsTopLeft;
  if (kind === 'requirements') return ListChecks;
  if (kind === 'interconnect') return Cable;
  if (kind === 'document') return FileText;
  return SquareStack;
}

/**
 * IDE / CAD-style horizontal sheet tab strip (think VS Code editor tabs or
 * AutoCAD model/layout tabs). Complements the project tree: the tree gives the
 * full hierarchy, these tabs give one-click switching + an always-visible "you
 * are here" for the active sheet. Sits directly under the command ribbon.
 */
export function SheetTabs() {
  const sheets = useStore((s) => s.sheets);
  const activeSheetId = useStore((s) => s.activeSheetId);
  const setActiveSheet = useStore((s) => s.setActiveSheet);
  const renameSheet = useStore((s) => s.renameSheet);
  const deleteSheet = useStore((s) => s.deleteSheet);
  const addSheet = useStore((s) => s.addSheet);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [dialog, setDialog] = useState<AddDialog>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const editRef = useRef<HTMLInputElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editingId) {
      editRef.current?.focus();
      editRef.current?.select();
    }
  }, [editingId]);

  // Keep the active tab scrolled into view.
  useEffect(() => {
    const el = stripRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [activeSheetId]);

  if (sheets.length === 0) return null;

  const startRename = (id: string, current: string) => {
    setEditingId(id);
    setDraft(current);
  };
  const commitRename = () => {
    if (editingId) {
      const v = draft.trim();
      if (v) renameSheet(editingId, v);
    }
    setEditingId(null);
  };

  return (
    <div style={stripStyle} ref={stripRef}>
      {sheets.map((sheet) => {
        const Icon = iconForKind(sheet.kind);
        const active = sheet.id === activeSheetId;
        const editing = editingId === sheet.id;
        return (
          <div
            key={sheet.id}
            data-active={active ? 'true' : undefined}
            onMouseDown={(e) => {
              // middle-click closes (IDE convention)
              if (e.button === 1 && sheets.length > 1) {
                e.preventDefault();
                setPendingDelete({ id: sheet.id, name: sheet.name });
              }
            }}
            onClick={() => !editing && setActiveSheet(sheet.id)}
            onDoubleClick={() => startRename(sheet.id, sheet.name)}
            style={{ ...tabStyle, ...(active ? tabActiveStyle : {}) }}
            title={sheet.name}
          >
            <Icon size={13} style={{ color: active ? 'var(--accent)' : 'var(--text-muted)', flexShrink: 0 }} />
            {editing ? (
              <input
                ref={editRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  else if (e.key === 'Escape') setEditingId(null);
                }}
                style={tabInputStyle}
              />
            ) : (
              <span style={tabNameStyle}>{sheet.name || 'Untitled'}</span>
            )}
            {sheets.length > 1 && !editing && (
              <button
                style={tabCloseStyle}
                title="Close sheet"
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingDelete({ id: sheet.id, name: sheet.name });
                }}
              >
                <X size={12} />
              </button>
            )}
          </div>
        );
      })}

      <button style={addTabStyle} title="Add sheet / subsystem" onClick={() => setDialog({ mode: 'pickKind' })}>
        <Plus size={15} />
      </button>

      {/* ---- add-sheet flow (mirrors the project tree) ---- */}
      {dialog?.mode === 'pickKind' && (
        <PickKindModal
          onCancel={() => setDialog(null)}
          onPick={(kind) => {
            if (kind === 'subsystem') {
              setDialog({ mode: 'subsystem', defaultName: 'Subsystem' });
              return;
            }
            const defaultName =
              kind === 'block'
                ? 'Block Diagram'
                : kind === 'mechanical'
                  ? 'Mechanical Layout'
                  : kind === 'requirements'
                    ? 'Requirements'
                    : kind === 'interconnect'
                      ? 'Harnesses'
                      : kind === 'document'
                        ? 'Maintenance Manual'
                        : 'Schematic';
            setDialog({ mode: 'sheet', kind, defaultName });
          }}
        />
      )}
      {dialog?.mode === 'subsystem' && (
        <NameModal
          title="Add subsystem"
          description={
            'Adds a Requirements + Block + Schematic + Mechanical sheet set to the project.\n\nExamples: "Robot", "Control Box", "Sensor Module".'
          }
          defaultValue={dialog.defaultName}
          okLabel="Add subsystem"
          onCancel={() => setDialog(null)}
          onSubmit={(name) => {
            addSheet('requirements', `${name} — Requirements`);
            addSheet('block', `${name} — Block`);
            addSheet('schematic', `${name} — Schematic`);
            addSheet('mechanical', `${name} — Mechanical`);
            setDialog(null);
          }}
        />
      )}
      {dialog?.mode === 'sheet' && (
        <NameModal
          title={`Add ${dialog.kind} sheet`}
          defaultValue={dialog.defaultName}
          onCancel={() => setDialog(null)}
          onSubmit={(name) => {
            addSheet(dialog.kind, name);
            setDialog(null);
          }}
        />
      )}

      {pendingDelete && (
        <ConfirmModal
          title="Close sheet"
          message={`Delete "${pendingDelete.name}"? This removes the sheet and its contents.`}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            deleteSheet(pendingDelete.id);
            setPendingDelete(null);
          }}
        />
      )}
    </div>
  );
}

const stripStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  gap: 0,
  height: 30,
  flexShrink: 0,
  background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
  borderBottom: '1px solid var(--chrome-border)',
  overflowX: 'auto',
  overflowY: 'hidden',
};

const tabStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '0 8px 0 11px',
  height: '100%',
  maxWidth: 220,
  borderRight: '1px solid var(--chrome-border)',
  borderTop: '2px solid transparent',
  color: 'var(--text-muted)',
  fontSize: 12,
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
};

const tabActiveStyle: CSSProperties = {
  background: 'var(--bg-canvas)',
  color: 'var(--text)',
  borderTop: '2px solid var(--accent)',
  fontWeight: 700,
};

const tabNameStyle: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const tabInputStyle: CSSProperties = {
  width: 120,
  border: '1px solid var(--accent)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text)',
  fontSize: 12,
  fontWeight: 700,
  padding: '1px 4px',
  outline: 'none',
  fontFamily: 'inherit',
};

const tabCloseStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
  marginLeft: 2,
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--text-dim)',
  cursor: 'pointer',
  padding: 0,
};

const addTabStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: '100%',
  border: 'none',
  borderRight: '1px solid var(--chrome-border)',
  background: 'transparent',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  flexShrink: 0,
};
